import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { FieldSelect } from "components/AtomsV2";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetEMSBaselineQuery,
  useGetEMSMemberEnergyQuery,
} from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

const ALL_CPES = "__all__";

function BaselineChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const queryArgs = {
    communityId,
    moduleType,
    startDatetime,
    endDatetime,
    limit: startDatetime || endDatetime ? 2000 : 1000,
    offset: 0,
  };
  const queryOpts = { skip: !communityId, pollingInterval: 30000 };

  const {
    data: baselineList = [],
    isFetching: isFetchingBaseline,
    currentData: currentDataBaseline,
  } = useGetEMSBaselineQuery(queryArgs, queryOpts);
  // Pending for the *current* args (date range / module); a background
  // polling refetch keeps currentData and therefore does not flash a spinner.
  const isLoadingBaseline = isFetchingBaseline && currentDataBaseline === undefined;
  const {
    data: memberEnergyList = [],
    isFetching: isFetchingEnergy,
    currentData: currentDataEnergy,
  } = useGetEMSMemberEnergyQuery(queryArgs, queryOpts);
  // Pending for the *current* args (date range / module); a background
  // polling refetch keeps currentData and therefore does not flash a spinner.
  const isLoadingEnergy = isFetchingEnergy && currentDataEnergy === undefined;

  const isEmpty = !isLoadingBaseline && !isLoadingEnergy && baselineList.length === 0;

  useEffect(() => {
    if (onDataStatus) onDataStatus(isLoadingBaseline || isLoadingEnergy ? null : !isEmpty);
  }, [isLoadingBaseline, isLoadingEnergy, isEmpty, onDataStatus]);

  const [selectedCpe, setSelectedCpe] = useState(null);

  const cpes = useMemo(() => {
    const map = new Map();
    baselineList.forEach((r) => {
      if (r.cpe_id && !map.has(r.cpe_id)) map.set(r.cpe_id, r.cpe_id);
    });
    memberEnergyList.forEach((r) => {
      if (r.cpe_id && !map.has(r.cpe_id)) map.set(r.cpe_id, r.cpe_id);
    });
    return [...map.keys()];
  }, [baselineList, memberEnergyList]);

  const effectiveCpe = selectedCpe || (cpes.length > 1 ? ALL_CPES : cpes[0]);

  const baselineByTs = useMemo(() => {
    const src =
      effectiveCpe === ALL_CPES
        ? baselineList
        : baselineList.filter((r) => r.cpe_id === effectiveCpe);
    const m = new Map();
    src.forEach((r) =>
      m.set(r.datetime_timestamp, (m.get(r.datetime_timestamp) ?? 0) + (r.baseline ?? 0))
    );
    return m;
  }, [baselineList, effectiveCpe]);

  const ecmetByTs = useMemo(() => {
    const src =
      effectiveCpe === ALL_CPES
        ? memberEnergyList
        : memberEnergyList.filter((r) => r.cpe_id === effectiveCpe);
    const m = new Map();
    src.forEach((r) =>
      m.set(r.datetime_timestamp, (m.get(r.datetime_timestamp) ?? 0) + (r.e_cmet ?? 0))
    );
    return m;
  }, [memberEnergyList, effectiveCpe]);

  const { series, labels } = useMemo(() => {
    const allTs = new Set([...baselineByTs.keys(), ...ecmetByTs.keys()]);
    if (allTs.size === 0) return { series: [], labels: [] };

    const sorted = [...allTs].sort((a, b) => new Date(a) - new Date(b));
    const lbls = sorted.map((ts) => createChartXAxisLabel(ts, "MIN15"));

    return {
      labels: lbls,
      series: [
        {
          name: t("unified_ems.baseline_chart_baseline_label"),
          data: sorted.map((ts) => {
            const v = baselineByTs.get(ts);
            return v !== undefined ? Number(v.toFixed(4)) : null;
          }),
          color: "var(--info)",
        },
        {
          name: t("unified_ems.baseline_chart_actual_label"),
          data: sorted.map((ts) => {
            const v = ecmetByTs.get(ts);
            return v !== undefined ? Number(v.toFixed(4)) : null;
          }),
          color: "var(--warning)",
        },
      ],
    };
  }, [baselineByTs, ecmetByTs, t]);

  if (isLoadingBaseline || isLoadingEnergy || isEmpty) return null;

  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  const toolbar =
    cpes.length > 1 ? (
      <FieldSelect
        value={effectiveCpe}
        onChange={(v) => setSelectedCpe(v)}
        options={[
          { value: ALL_CPES, label: t("unified_ems.chart_community_aggregate") },
          ...cpes.map((id) => ({ value: id, label: id })),
        ]}
      />
    ) : null;

  return (
    <EmsChartCard
      title={t("unified_ems.baseline_chart_title")}
      legend={legend}
      toolbar={toolbar}
      series={series}
      labels={labels}
      unit="kWh"
      type="line"
    />
  );
}

BaselineChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};

BaselineChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default BaselineChart;
